import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { useCart } from '../context/CartContext';
import { storefront, supabase } from '../lib/supabase';
import { formatVND, pickLang } from '../lib/format';
import { useOfferMath } from './Cart';

export default function Checkout() {
  const { lang, t } = useLang();
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();

  const [tiers, setTiers] = useState([]);
  const [payCfg, setPayCfg] = useState({});
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    hotel_name: '', room_number: '', delivery_address: '', delivery_time: '',
    payment_method: 'cod', note: '',
  });
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    storefront.offerTiers().then(({ data }) => setTiers(data || []));
    storefront.content().then(({ data }) => {
      setPayCfg((data || []).find((x) => x.key === 'payment')?.extra || {});
    });
  }, []);

  const { discount, total, reached } = useOfferMath(subtotal, tiers);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const qrUrl =
    payCfg.bank_id && payCfg.account_no
      ? `https://img.vietqr.io/image/${payCfg.bank_id}-${payCfg.account_no}-compact2.png?amount=${Math.round(total)}&addInfo=${encodeURIComponent(`${payCfg.note_prefix || 'LINT'} ${form.customer_phone || ''}`.trim())}&accountName=${encodeURIComponent(payCfg.account_name || '')}`
      : null;

  const placeOrder = async () => {
    setError('');
    if (!form.customer_name.trim() || !form.customer_phone.trim() || (!form.hotel_name.trim() && !form.delivery_address.trim())) {
      setError(t('co.required'));
      return;
    }
    setPlacing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const order = {
        user_id: session?.user?.id ?? null,
        items: items.map((x) => ({ kind: x.kind, id: x.id, name: x.name, price: x.price, qty: x.qty })),
        total,
        status: 'pending',
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        customer_email: form.customer_email.trim() || null,
        hotel_name: form.hotel_name.trim() || null,
        room_number: form.room_number.trim() || null,
        delivery_address: form.delivery_address.trim() || null,
        delivery_time: form.delivery_time.trim() || null,
        payment_method: form.payment_method,
        payment_status: 'pending',
        admin_note: form.note.trim() || null,
      };
      const { data, error: e } = await storefront.createOrder(order);
      if (e) throw e;
      clear();
      navigate('/order-success', { state: { orderId: data.id } });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setPlacing(false);
    }
  };

  if (!items.length) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="container section" style={{ maxWidth: 720 }}>
      <h1>🏨 {t('co.title')}</h1>

      <div className="card" style={{ marginBottom: 16 }}>
        {items.map((x) => (
          <div className="row spread" key={`${x.kind}-${x.id}`}>
            <span>{lang === 'en' && x.name_en ? x.name_en : x.name} × {x.qty}</span>
            <span>{formatVND(x.price * x.qty)}</span>
          </div>
        ))}
        {discount > 0 && (
          <div className="row spread" style={{ color: 'var(--green)' }}>
            <span>{t('cart.discount')} — {pickLang(reached, 'reward', lang)}</span>
            <span>−{formatVND(discount)}</span>
          </div>
        )}
        <hr style={{ border: 'none', borderTop: '1px solid var(--hairline-soft)' }} />
        <div className="row spread" style={{ fontSize: 18 }}>
          <strong>{t('cart.total')}</strong><strong className="price">{formatVND(total)}</strong>
        </div>
      </div>

      <div className="card">
        <div className="form-grid">
          <label className="field"><span>{t('co.name')} *</span>
            <input value={form.customer_name} onChange={set('customer_name')} /></label>
          <label className="field"><span>{t('co.phone')} *</span>
            <input value={form.customer_phone} onChange={set('customer_phone')} /></label>
          <label className="field"><span>{t('co.hotel')} *</span>
            <input value={form.hotel_name} onChange={set('hotel_name')} placeholder="VD: Sheraton Nha Trang" /></label>
          <label className="field"><span>{t('co.room')}</span>
            <input value={form.room_number} onChange={set('room_number')} /></label>
          <label className="field"><span>{t('co.time')}</span>
            <input value={form.delivery_time} onChange={set('delivery_time')} placeholder="VD: 15:00 - 17:00" /></label>
          <label className="field"><span>{t('co.email')}</span>
            <input value={form.customer_email} onChange={set('customer_email')} /></label>
          <label className="field full"><span>{t('co.address')}</span>
            <input value={form.delivery_address} onChange={set('delivery_address')} /></label>
          <label className="field full"><span>{t('co.note')}</span>
            <textarea value={form.note} onChange={set('note')} style={{ minHeight: 60 }} /></label>
        </div>

        <h4 style={{ marginTop: 6 }}>{t('co.payment')}</h4>
        <label className="checkbox-row">
          <input type="radio" name="pay" checked={form.payment_method === 'cod'}
            onChange={() => setForm((p) => ({ ...p, payment_method: 'cod' }))} />
          💵 {t('co.cod')}
        </label>
        <label className="checkbox-row">
          <input type="radio" name="pay" checked={form.payment_method === 'bank_transfer'}
            onChange={() => setForm((p) => ({ ...p, payment_method: 'bank_transfer' }))} />
          🏦 {t('co.bank')}
        </label>

        {form.payment_method === 'bank_transfer' && (
          <div className="banner banner-blue">
            <div style={{ marginBottom: 8 }}>{t('co.qrNote')}</div>
            {qrUrl && (
              <img src={qrUrl} alt="VietQR" style={{ maxWidth: 260, borderRadius: 8, background: '#fff' }} />
            )}
            <div className="muted" style={{ marginTop: 6 }}>
              {payCfg.bank_id} · {payCfg.account_no} · {payCfg.account_name} · {formatVND(total)}
            </div>
          </div>
        )}

        {error && <div className="banner" style={{ background: 'var(--red-soft)', color: 'var(--red)' }}>{error}</div>}

        <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={placeOrder} disabled={placing}>
          {placing ? t('co.placing') : `${t('co.placeOrder')} — ${formatVND(total)}`}
        </button>
      </div>
    </div>
  );
}
